import React, { useEffect, useMemo, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from "reactstrap"
import classnames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import Breadcrumbs from "components/Common/Breadcrumb"
import ConfirmModal from "components/Common/ConfirmModal"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import {
  saGetAllIntegrationCategories,
  saGetAllIntegrations,
  saCreateIntegrationCategory,
  saUpdateIntegrationCategory,
  saDeleteIntegrationCategory,
  saUpdateIntegrationCategoryStatus,
  saCreateIntegration,
  saUpdateIntegration,
  saDeleteIntegration,
  saUpdateIntegrationStatus,
} from "store/actions/superAdminAction"
import { showAlert } from "store/reducers/alertReducer"
import isPermissionEnabled, {
  isSaIntegrationActionEnabled,
} from "common/isPermissionEnabled"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import { localizedBilingualText, translateStatusLabel } from "utils/statusLabels"

/** Super-admin role catalog category (see `superAdminPermissionsList` in constants). */
const SA_INTEGRATIONS_CATEGORY = 107

const defaultCategoryForm = {
  _id: null,
  code: "",
  titleEn: "",
  titleAr: "",
  descriptionEn: "",
  descriptionAr: "",
  status: "active",
}

const defaultIntegrationForm = {
  _id: null,
  code: "",
  categoryId: "",
  titleEn: "",
  titleAr: "",
  descriptionEn: "",
  descriptionAr: "",
  icon: "",
  status: "active",
  credentialSchemaText: JSON.stringify({ fields: [] }, null, 2),
  connectionMetaSchemaText: JSON.stringify({}, null, 2),
}

const SYRVE_CREDENTIAL_SCHEMA_DEMO = {
  fields: [
    {
      key: "apiBaseUrl",
      label: { en: "API Base URL", ar: "رابط الـ API" },
      type: "text",
      required: true,
      placeholder: {
        en: "https://api.syrve.live",
        ar: "https://api.syrve.live",
      },
    },
    {
      key: "username",
      label: { en: "Username", ar: "اسم المستخدم" },
      type: "text",
      required: true,
      placeholder: {
        en: "syrve_api_user",
        ar: "اسم مستخدم Syrve",
      },
    },
    {
      key: "password",
      label: { en: "Password", ar: "كلمة المرور" },
      type: "password",
      required: true,
      placeholder: {
        en: "Enter password",
        ar: "أدخل كلمة المرور",
      },
    },
    {
      key: "organizationId",
      label: { en: "Organization ID", ar: "معرّف المؤسسة" },
      type: "text",
      required: true,
      placeholder: {
        en: "GUID of the Syrve organization",
        ar: "معرّف مؤسسة Syrve",
      },
    },
    {
      key: "environment",
      label: { en: "Environment", ar: "البيئة" },
      type: "select",
      required: true,
      options: [
        { value: "production", label: { en: "Production", ar: "إنتاج" } },
        { value: "sandbox", label: { en: "Sandbox", ar: "تجريبي" } },
      ],
    },
  ],
}

const SYRVE_CONNECTION_META_DEMO = {
  webhookUrl: "https://your-domain.com/webhooks/syrve",
  syncIntervalMinutes: 15,
  features: {
    menuSync: true,
    orderPush: true,
    inventorySync: false,
    customerSync: false,
  },
  defaultTerminalId: "",
  timezone: "Asia/Riyadh",
}

const IntegrationsManagement = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  document.title = t("Integrations | Super Admin")
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const {
    integrationCategoriesLoading,
    integrationCategoriesActionLoading,
    integrationCategories,
    integrationsLoading,
    integrationsActionLoading,
    integrations,
  } = useSelector(state => state.SuperAdmin)

  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login || {}
  )

  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"

  const canSaIntegrationsView = isPermissionEnabled(
    userRole,
    SA_INTEGRATIONS_CATEGORY,
    "sa_integrations_1",
    accountType,
    accountScope
  )
  const canSaAddIntegration = isSaIntegrationActionEnabled(
    userRole,
    "sa_integrations_2_add",
    accountType,
    accountScope
  )
  const canSaEditIntegration = isSaIntegrationActionEnabled(
    userRole,
    "sa_integrations_2_edit",
    accountType,
    accountScope
  )
  const canSaChangeIntegrationStatus = isSaIntegrationActionEnabled(
    userRole,
    "sa_integrations_2_status",
    accountType,
    accountScope
  )
  const canSaDeleteIntegrationCatalog = isSaIntegrationActionEnabled(
    userRole,
    "sa_integrations_4_integration",
    accountType,
    accountScope
  )
  const canSaAddCategory = isSaIntegrationActionEnabled(
    userRole,
    "sa_integrations_3_add",
    accountType,
    accountScope
  )
  const canSaEditCategory = isSaIntegrationActionEnabled(
    userRole,
    "sa_integrations_3_edit",
    accountType,
    accountScope
  )
  const canSaDeleteCategoryCatalog = isSaIntegrationActionEnabled(
    userRole,
    "sa_integrations_4_category",
    accountType,
    accountScope
  )
  const canSaUpdateCategoryStatus = isPermissionEnabled(
    userRole,
    SA_INTEGRATIONS_CATEGORY,
    "sa_integrations_5",
    accountType,
    accountScope
  )

  const [activeCategoryId, setActiveCategoryId] = useState("")
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)
  const [integrationModalOpen, setIntegrationModalOpen] = useState(false)
  const [categoryForm, setCategoryForm] = useState(defaultCategoryForm)
  const [integrationForm, setIntegrationForm] = useState(defaultIntegrationForm)
  const [iconUrlType, setIconUrlType] = useState("URL")
  const [iconFile, setIconFile] = useState(null)
  const [iconFilePreview, setIconFilePreview] = useState("")
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    targetType: "",
    action: "",
    data: null,
  })

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (!canSaIntegrationsView) {
      navigateToAccessibleRoute(navigate)
      return
    }
    dispatch(saGetAllIntegrationCategories({ page: 1, limit: 100 }))
    dispatch(saGetAllIntegrations({ page: 1, limit: 500 }))
  }, [dispatch, isSuperAdminSession, canSaIntegrationsView, navigate])

  useEffect(() => {
    if (!activeCategoryId && integrationCategories?.length > 0) {
      setActiveCategoryId(integrationCategories[0]._id)
    }
  }, [integrationCategories, activeCategoryId])

  useEffect(() => {
    return () => {
      if (iconFilePreview) {
        URL.revokeObjectURL(iconFilePreview)
      }
    }
  }, [iconFilePreview])

  const filteredIntegrations = useMemo(() => {
    if (!activeCategoryId) return integrations || []
    return (integrations || []).filter(intg => {
      const categoryId = intg?.categoryId?._id || intg?.categoryId
      return String(categoryId) === String(activeCategoryId)
    })
  }, [integrations, activeCategoryId])

  const refreshData = () => {
    dispatch(saGetAllIntegrationCategories({ page: 1, limit: 100 }))
    dispatch(saGetAllIntegrations({ page: 1, limit: 500 }))
  }

  const resetIconUploadState = () => {
    if (iconFilePreview) {
      URL.revokeObjectURL(iconFilePreview)
    }
    setIconFile(null)
    setIconFilePreview("")
    setIconUrlType("URL")
  }

  const handleIconTypeChange = type => {
    setIconUrlType(type)
    if (type === "URL") {
      setIconFile(null)
      if (iconFilePreview) {
        URL.revokeObjectURL(iconFilePreview)
        setIconFilePreview("")
      }
    }
  }

  const handleIconFileChange = e => {
    const file = e.target.files?.[0]
    if (!file) return
    if (iconFilePreview) {
      URL.revokeObjectURL(iconFilePreview)
    }
    setIconFile(file)
    setIconFilePreview(URL.createObjectURL(file))
  }

  const iconPreviewSrc = iconFilePreview || integrationForm.icon || ""

  const copyDemoToField = (fieldKey, demoObject) => {
    const text = JSON.stringify(demoObject, null, 2)
    setIntegrationForm(prev => ({ ...prev, [fieldKey]: text }))
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {})
    }
    dispatch(
      showAlert({
        type: "success",
        message: t("Demo data copied"),
      })
    )
  }

  const openCreateCategoryModal = () => {
    if (!canSaAddCategory) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Sorry, you don't have permission to access this resource."),
        })
      )
      return
    }
    setCategoryForm(defaultCategoryForm)
    setCategoryModalOpen(true)
  }

  const openEditCategoryModal = category => {
    if (!canSaEditCategory) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Sorry, you don't have permission to access this resource."),
        })
      )
      return
    }
    setCategoryForm({
      _id: category._id,
      code: category.code || "",
      titleEn: category?.title?.en || "",
      titleAr: category?.title?.ar || "",
      descriptionEn: category?.description?.en || "",
      descriptionAr: category?.description?.ar || "",
      status: category.status || "active",
    })
    setCategoryModalOpen(true)
  }

  const openCreateIntegrationModal = () => {
    if (!canSaAddIntegration) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Sorry, you don't have permission to access this resource."),
        })
      )
      return
    }
    resetIconUploadState()
    setIntegrationForm({
      ...defaultIntegrationForm,
      categoryId:
        activeCategoryId ||
        integrationCategories?.[0]?._id ||
        "",
    })
    setIntegrationModalOpen(true)
  }

  const openEditIntegrationModal = integration => {
    if (!canSaEditIntegration) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Sorry, you don't have permission to access this resource."),
        })
      )
      return
    }
    resetIconUploadState()
    setIntegrationForm({
      _id: integration._id,
      code: integration.code || "",
      categoryId: integration?.categoryId?._id || integration?.categoryId || "",
      titleEn: integration?.title?.en || "",
      titleAr: integration?.title?.ar || "",
      descriptionEn: integration?.description?.en || "",
      descriptionAr: integration?.description?.ar || "",
      icon: integration?.icon || "",
      status: integration.status || "active",
      credentialSchemaText: JSON.stringify(
        integration?.credentialSchema || { fields: [] },
        null,
        2
      ),
      connectionMetaSchemaText: JSON.stringify(
        integration?.connectionMetaSchema || {},
        null,
        2
      ),
    })
    setIntegrationModalOpen(true)
  }

  const submitCategoryForm = e => {
    e.preventDefault()
    if (categoryForm._id) {
      if (!canSaEditCategory) {
        dispatch(
          showAlert({
            type: "error",
            message: t("Sorry, you don't have permission to access this resource."),
          })
        )
        return
      }
    } else if (!canSaAddCategory) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Sorry, you don't have permission to access this resource."),
        })
      )
      return
    }
    const payload = {
      code: categoryForm.code,
      title: {
        en: categoryForm.titleEn.trim(),
        ar: categoryForm.titleAr.trim(),
      },
      description:
        categoryForm.descriptionEn || categoryForm.descriptionAr
          ? {
              en: categoryForm.descriptionEn.trim(),
              ar: categoryForm.descriptionAr.trim(),
            }
          : undefined,
      status: categoryForm.status,
    }

    const action = categoryForm._id
      ? saUpdateIntegrationCategory({ ...payload, id: categoryForm._id })
      : saCreateIntegrationCategory(payload)

    dispatch(action).then(res => {
      if (res?.payload?.status) {
        dispatch(
          showAlert({
            type: "success",
            message: categoryForm._id
              ? t("Category updated successfully")
              : t("Category created successfully"),
          })
        )
        setCategoryModalOpen(false)
        refreshData()
      } else {
        dispatch(
          showAlert({
            type: "error",
            message: res?.payload?.msg || t("Unable to save category"),
          })
        )
      }
    })
  }

  const submitIntegrationForm = e => {
    e.preventDefault()
    if (integrationForm._id) {
      if (!canSaEditIntegration) {
        dispatch(
          showAlert({
            type: "error",
            message: t("Sorry, you don't have permission to access this resource."),
          })
        )
        return
      }
    } else if (!canSaAddIntegration) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Sorry, you don't have permission to access this resource."),
        })
      )
      return
    }
    let credentialSchema = { fields: [] }
    let connectionMetaSchema = {}

    try {
      credentialSchema = JSON.parse(integrationForm.credentialSchemaText || "{}")
      connectionMetaSchema = JSON.parse(integrationForm.connectionMetaSchemaText || "{}")
    } catch (_error) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Credential schema and connection meta must be valid JSON"),
        })
      )
      return
    }

    const title = {
      en: integrationForm.titleEn.trim(),
      ar: integrationForm.titleAr.trim(),
    }
    const description =
      integrationForm.descriptionEn || integrationForm.descriptionAr
        ? {
            en: integrationForm.descriptionEn.trim(),
            ar: integrationForm.descriptionAr.trim(),
          }
        : undefined

    if (iconUrlType === "UPLOAD" && !iconFile && !integrationForm.icon) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Please select an icon image to upload"),
        })
      )
      return
    }

    const buildJsonPayload = () => ({
      code: integrationForm.code,
      categoryId: integrationForm.categoryId,
      title,
      description,
      icon: integrationForm.icon,
      status: integrationForm.status,
      credentialSchema,
      connectionMetaSchema,
    })

    const buildFormDataPayload = () => {
      const data = new FormData()
      data.append("code", integrationForm.code || "")
      data.append("categoryId", integrationForm.categoryId)
      data.append("title", JSON.stringify(title))
      if (description) {
        data.append("description", JSON.stringify(description))
      }
      data.append("status", integrationForm.status)
      data.append("credentialSchema", JSON.stringify(credentialSchema))
      data.append("connectionMetaSchema", JSON.stringify(connectionMetaSchema))
      data.append("iconUrlType", "UPLOAD")
      if (iconFile) {
        data.append("image", iconFile)
      }
      return data
    }

    let payload =
      iconUrlType === "UPLOAD" && iconFile
        ? buildFormDataPayload()
        : buildJsonPayload()

    if (integrationForm._id) {
      if (payload instanceof FormData) {
        payload.append("id", integrationForm._id)
      } else {
        payload = { ...payload, id: integrationForm._id }
      }
    }

    const action = integrationForm._id
      ? saUpdateIntegration(payload)
      : saCreateIntegration(payload)

    dispatch(action).then(res => {
      if (res?.payload?.status) {
        dispatch(
          showAlert({
            type: "success",
            message: integrationForm._id
              ? t("Integration updated successfully")
              : t("Integration created successfully"),
          })
        )
        setIntegrationModalOpen(false)
        refreshData()
      } else {
        dispatch(
          showAlert({
            type: "error",
            message: res?.payload?.msg || t("Unable to save integration"),
          })
        )
      }
    })
  }

  const confirmAction = () => {
    const { targetType, action, data } = confirmModal
    let thunk = null

    if (targetType === "category" && action === "delete") {
      if (!canSaDeleteCategoryCatalog) {
        dispatch(
          showAlert({
            type: "error",
            message: t("Sorry, you don't have permission to access this resource."),
          })
        )
        setConfirmModal({ open: false, targetType: "", action: "", data: null })
        return
      }
      thunk = saDeleteIntegrationCategory({ id: data?._id })
    } else if (targetType === "category" && action === "status") {
      if (!canSaUpdateCategoryStatus) {
        dispatch(
          showAlert({
            type: "error",
            message: t("Sorry, you don't have permission to access this resource."),
          })
        )
        setConfirmModal({ open: false, targetType: "", action: "", data: null })
        return
      }
      thunk = saUpdateIntegrationCategoryStatus({
        id: data?._id,
        status: data?.status === "active" ? "inactive" : "active",
      })
    } else if (targetType === "integration" && action === "delete") {
      if (!canSaDeleteIntegrationCatalog) {
        dispatch(
          showAlert({
            type: "error",
            message: t("Sorry, you don't have permission to access this resource."),
          })
        )
        setConfirmModal({ open: false, targetType: "", action: "", data: null })
        return
      }
      thunk = saDeleteIntegration({ id: data?._id })
    } else if (targetType === "integration" && action === "status") {
      if (!canSaChangeIntegrationStatus) {
        dispatch(
          showAlert({
            type: "error",
            message: t("Sorry, you don't have permission to access this resource."),
          })
        )
        setConfirmModal({ open: false, targetType: "", action: "", data: null })
        return
      }
      thunk = saUpdateIntegrationStatus({
        id: data?._id,
        status: data?.status === "active" ? "inactive" : "active",
      })
    }

    if (!thunk) {
      setConfirmModal({ open: false, targetType: "", action: "", data: null })
      return
    }

    dispatch(thunk).then(res => {
      if (res?.payload?.status) {
        dispatch(
          showAlert({
            type: "success",
            message: t("Operation completed successfully"),
          })
        )
        refreshData()
      } else {
        dispatch(
          showAlert({
            type: "error",
            message: res?.payload?.msg || t("Operation failed"),
          })
        )
      }
    })
    setConfirmModal({ open: false, targetType: "", action: "", data: null })
  }

  if (!canSaIntegrationsView) {
    return null
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row className="mb-3">
            <Col>
              <h4 className="mb-0">{t("Integrations")}</h4>
              <p className="text-muted mb-0">
                {t("Manage integration categories and available integrations")}
              </p>
            </Col>
            <Col xs="auto" className="d-flex gap-2">
              {canSaAddCategory ? (
                <Button color="light" onClick={openCreateCategoryModal}>
                  <i className="bx bx-category-alt me-1" />
                  {t("Add Category")}
                </Button>
              ) : null}
              {canSaAddIntegration ? (
                <Button color="primary" onClick={openCreateIntegrationModal}>
                  <i className="bx bx-plus me-1" />
                  {t("Add Integration")}
                </Button>
              ) : null}
            </Col>
          </Row>

          <Card>
            <CardBody>
              {integrationCategoriesLoading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                </div>
              ) : integrationCategories.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  {canSaAddCategory
                    ? t("No integration categories found. Add your first category.")
                    : t("No integration categories found.")}
                  {canSaAddCategory ? (
                    <div className="mt-3">
                      <Button color="primary" onClick={openCreateCategoryModal}>
                        <i className="bx bx-category-alt me-1" />
                        {t("Add Category")}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  <Nav tabs className="mb-3">
                    {integrationCategories.map(category => (
                      <NavItem key={category._id}>
                        <NavLink
                          href="#"
                          onClick={e => {
                            e.preventDefault()
                            setActiveCategoryId(category._id)
                          }}
                          className={classnames({
                            active: String(activeCategoryId) === String(category._id),
                          })}
                        >
                          {localizedBilingualText(category?.title, language, category.code)}
                          <Badge
                            color={category.status === "active" ? "success" : "secondary"}
                            className="ms-2"
                          >
                            {translateStatusLabel(t, category.status)}
                          </Badge>
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">{t("Category Controls")}</h5>
                    {activeCategoryId &&
                    (canSaAddCategory ||
                      canSaEditCategory ||
                      canSaUpdateCategoryStatus ||
                      canSaDeleteCategoryCatalog) ? (
                      <div className="d-flex gap-2">
                        {canSaEditCategory ? (
                          <Button
                            size="sm"
                            color="light"
                            onClick={() =>
                              openEditCategoryModal(
                                integrationCategories.find(
                                  c => c._id === activeCategoryId
                                )
                              )
                            }
                          >
                            <i className="bx bx-edit me-1" />
                            {t("Edit Category")}
                          </Button>
                        ) : null}
                        {canSaUpdateCategoryStatus ? (
                          <Button
                            size="sm"
                            color="light"
                            onClick={() =>
                              setConfirmModal({
                                open: true,
                                targetType: "category",
                                action: "status",
                                data: integrationCategories.find(
                                  c => c._id === activeCategoryId
                                ),
                              })
                            }
                          >
                            <i className="bx bx-power-off me-1" />
                            {t("Toggle Status")}
                          </Button>
                        ) : null}
                        {canSaDeleteCategoryCatalog ? (
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            onClick={() =>
                              setConfirmModal({
                                open: true,
                                targetType: "category",
                                action: "delete",
                                data: integrationCategories.find(
                                  c => c._id === activeCategoryId
                                ),
                              })
                            }
                          >
                            <i className="bx bx-trash me-1" />
                            {t("Delete Category")}
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  {integrationsLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                    </div>
                  ) : filteredIntegrations.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      {t("No integrations found for this category.")}
                    </div>
                  ) : (
                    <Row className="g-3">
                      {filteredIntegrations.map(integration => (
                        <Col lg={4} md={6} key={integration._id}>
                          <Card className="h-100">
                            <CardBody>
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h5 className="mb-1">
                                    {localizedBilingualText(
                                      integration?.title,
                                      language,
                                      integration.code
                                    )}
                                  </h5>
                                  <p className="text-muted mb-2">
                                    {localizedBilingualText(
                                      integration?.description,
                                      language,
                                      t("No description")
                                    )}
                                  </p>
                                </div>
                                <Badge color={integration.status === "active" ? "success" : "secondary"}>
                                  {translateStatusLabel(t, integration.status)}
                                </Badge>
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                {canSaEditIntegration ? (
                                  <Button
                                    size="sm"
                                    color="light"
                                    onClick={() =>
                                      openEditIntegrationModal(integration)
                                    }
                                  >
                                    <i className="bx bx-edit me-1" />
                                    {t("Edit")}
                                  </Button>
                                ) : null}
                                {canSaChangeIntegrationStatus ? (
                                  <Button
                                    size="sm"
                                    color="light"
                                    onClick={() =>
                                      setConfirmModal({
                                        open: true,
                                        targetType: "integration",
                                        action: "status",
                                        data: integration,
                                      })
                                    }
                                  >
                                    <i className="bx bx-power-off me-1" />
                                    {t("Toggle Status")}
                                  </Button>
                                ) : null}
                                {canSaDeleteIntegrationCatalog ? (
                                  <Button
                                    size="sm"
                                    color="danger"
                                    outline
                                    onClick={() =>
                                      setConfirmModal({
                                        open: true,
                                        targetType: "integration",
                                        action: "delete",
                                        data: integration,
                                      })
                                    }
                                  >
                                    <i className="bx bx-trash me-1" />
                                    {t("Delete")}
                                  </Button>
                                ) : null}
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={categoryModalOpen} toggle={() => setCategoryModalOpen(false)}>
        <Form onSubmit={submitCategoryForm}>
          <ModalHeader toggle={() => setCategoryModalOpen(false)}>
            {categoryForm._id ? t("Edit Category") : t("Add Category")}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>{t("Code")}</Label>
              <Input
                value={categoryForm.code}
                onChange={e => setCategoryForm(prev => ({ ...prev, code: e.target.value }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("Title (EN)")}</Label>
              <Input
                required
                value={categoryForm.titleEn}
                onChange={e => setCategoryForm(prev => ({ ...prev, titleEn: e.target.value }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("Title (AR)")}</Label>
              <Input
                required
                value={categoryForm.titleAr}
                onChange={e => setCategoryForm(prev => ({ ...prev, titleAr: e.target.value }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("Description (EN)")}</Label>
              <Input
                type="textarea"
                value={categoryForm.descriptionEn}
                onChange={e => setCategoryForm(prev => ({ ...prev, descriptionEn: e.target.value }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("Description (AR)")}</Label>
              <Input
                type="textarea"
                value={categoryForm.descriptionAr}
                onChange={e => setCategoryForm(prev => ({ ...prev, descriptionAr: e.target.value }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("Status")}</Label>
              <Input
                type="select"
                value={categoryForm.status}
                onChange={e => setCategoryForm(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="active">{t("Active")}</option>
                <option value="inactive">{t("Inactive")}</option>
              </Input>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setCategoryModalOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={integrationCategoriesActionLoading}>
              {integrationCategoriesActionLoading ? <Spinner size="sm" /> : t("Save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal size="lg" isOpen={integrationModalOpen} toggle={() => setIntegrationModalOpen(false)}>
        <Form onSubmit={submitIntegrationForm}>
          <ModalHeader toggle={() => setIntegrationModalOpen(false)}>
            {integrationForm._id ? t("Edit Integration") : t("Add Integration")}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Code")}</Label>
                  <Input
                    value={integrationForm.code}
                    onChange={e => setIntegrationForm(prev => ({ ...prev, code: e.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Category")}</Label>
                  <Input
                    type="select"
                    required
                    value={integrationForm.categoryId}
                    onChange={e =>
                      setIntegrationForm(prev => ({ ...prev, categoryId: e.target.value }))
                    }
                  >
                    <option value="">{t("Select category")}</option>
                    {integrationCategories.map(category => (
                      <option key={category._id} value={category._id}>
                        {localizedBilingualText(
                          category?.title,
                          language,
                          category.code
                        )}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Title (EN)")}</Label>
                  <Input
                    required
                    value={integrationForm.titleEn}
                    onChange={e => setIntegrationForm(prev => ({ ...prev, titleEn: e.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Title (AR)")}</Label>
                  <Input
                    required
                    value={integrationForm.titleAr}
                    onChange={e => setIntegrationForm(prev => ({ ...prev, titleAr: e.target.value }))}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Description (EN)")}</Label>
                  <Input
                    type="textarea"
                    value={integrationForm.descriptionEn}
                    onChange={e =>
                      setIntegrationForm(prev => ({ ...prev, descriptionEn: e.target.value }))
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Description (AR)")}</Label>
                  <Input
                    type="textarea"
                    value={integrationForm.descriptionAr}
                    onChange={e =>
                      setIntegrationForm(prev => ({ ...prev, descriptionAr: e.target.value }))
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Status")}</Label>
                  <Input
                    type="select"
                    value={integrationForm.status}
                    onChange={e => setIntegrationForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="active">{t("Active")}</option>
                    <option value="inactive">{t("Inactive")}</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Icon Type")}</Label>
                  <Input
                    type="select"
                    value={iconUrlType}
                    onChange={e => handleIconTypeChange(e.target.value)}
                  >
                    <option value="URL">{t("Image Url")}</option>
                    <option value="UPLOAD">{t("Upload Image")}</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            {iconUrlType === "URL" && (
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>{t("Icon URL")}</Label>
                    <Input
                      value={integrationForm.icon}
                      onChange={e =>
                        setIntegrationForm(prev => ({ ...prev, icon: e.target.value }))
                      }
                      placeholder="https://your-cdn.com/icon.png"
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
            {iconUrlType === "UPLOAD" && (
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>{t("Upload Image")}</Label>
                    <TranslatedFileInput
                      accept="image/jpeg, image/jpg, image/png, image/webp"
                      onChange={handleIconFileChange}
                    />
                    <small className="text-muted d-block pt-2">
                      {t("Accepted formats: JPG, JPEG, PNG")}
                    </small>
                  </FormGroup>
                </Col>
              </Row>
            )}
            {iconPreviewSrc && (
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>{t("Icon Preview")}</Label>
                    <div className="mt-2">
                      <img
                        src={iconPreviewSrc}
                        alt={t("Icon Preview")}
                        className="img_preview"
                        style={{ maxHeight: 80, maxWidth: 160 }}
                      />
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            )}
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Label className="mb-0">{t("Credential Schema (JSON)")}</Label>
                <Button
                  type="button"
                  size="sm"
                  color="light"
                  onClick={() =>
                    copyDemoToField(
                      "credentialSchemaText",
                      SYRVE_CREDENTIAL_SCHEMA_DEMO
                    )
                  }
                >
                  <i className="bx bx-copy me-1" />
                  {t("Copy demo (Syrve)")}
                </Button>
              </div>
              <Input
                type="textarea"
                rows={8}
                value={integrationForm.credentialSchemaText}
                onChange={e =>
                  setIntegrationForm(prev => ({
                    ...prev,
                    credentialSchemaText: e.target.value,
                  }))
                }
              />
              <small className="text-muted">
                {t(
                  "Each field's label / placeholder must be an object: { en, ar }."
                )}
              </small>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Label className="mb-0">{t("Connection Meta Schema (JSON)")}</Label>
                <Button
                  type="button"
                  size="sm"
                  color="light"
                  onClick={() =>
                    copyDemoToField(
                      "connectionMetaSchemaText",
                      SYRVE_CONNECTION_META_DEMO
                    )
                  }
                >
                  <i className="bx bx-copy me-1" />
                  {t("Copy demo (Syrve)")}
                </Button>
              </div>
              <Input
                type="textarea"
                rows={6}
                value={integrationForm.connectionMetaSchemaText}
                onChange={e =>
                  setIntegrationForm(prev => ({
                    ...prev,
                    connectionMetaSchemaText: e.target.value,
                  }))
                }
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setIntegrationModalOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={integrationsActionLoading}>
              {integrationsActionLoading ? <Spinner size="sm" /> : t("Save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ConfirmModal
        show={confirmModal.open}
        onCloseClick={() =>
          setConfirmModal({ open: false, targetType: "", action: "", data: null })
        }
        onDeleteClick={confirmAction}
        isLoading={integrationCategoriesActionLoading || integrationsActionLoading}
        buttonText={t("Confirm")}
        bodyTitle={t("Are you sure you want to continue?")}
      />
    </React.Fragment>
  )
}

export default IntegrationsManagement
